import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2, Palette, AtSign, Package, Users, ClipboardCheck, ArrowLeft, ArrowRight, Check,
  CalendarDays, FileText, ListChecks, Receipt, Minus, Plus, FileSignature,
} from 'lucide-react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useIsMobile } from '../hooks/useResponsive';

/**
 * Onboarding cliente: un percorso guidato che in un colpo solo crea il cliente,
 * salva brand e accessi nella scheda, prepara la checklist di avvio per il team
 * (con scadenze dal kick-off), mette il kick-off in calendario e abbozza il primo PED.
 */
const MANAGER = ['admin', 'boss', 'direttore'];

const PASSI = [
  { key: 'azienda', label: 'Azienda', Icon: Building2, desc: 'Chi è il cliente e come contattarlo.' },
  { key: 'brand', label: 'Brand', Icon: Palette, desc: 'Obiettivi, pubblico e stile: servono al team per partire bene.' },
  { key: 'social', label: 'Social e accessi', Icon: AtSign, desc: 'Profili da gestire e materiali già ricevuti.' },
  { key: 'servizi', label: 'Servizi', Icon: Package, desc: 'Cosa produciamo ogni mese: diventa la bozza del primo Piano Editoriale.' },
  { key: 'team', label: 'Team e avvio', Icon: Users, desc: 'Chi segue il cliente e quando si parte.' },
  { key: 'riepilogo', label: 'Riepilogo', Icon: ClipboardCheck, desc: 'Controlla e avvia l\'onboarding.' },
];

const SOCIAL = [['instagram', 'Instagram', '@nomeprofilo'], ['facebook', 'Facebook', 'facebook.com/…'], ['tiktok', 'TikTok', '@nomeprofilo'], ['linkedin', 'LinkedIn', 'linkedin.com/company/…'], ['youtube', 'YouTube', '@canale']];
const ACCESSI = [
  ['meta_business', 'Accesso a Meta Business Suite'],
  ['instagram', 'Accesso al profilo Instagram'],
  ['drive', 'Cartella condivisa per i materiali'],
  ['logo', 'Logo in alta risoluzione'],
  ['brand_kit', 'Brand kit (font, colori, linee guida)'],
];
const SERVIZI_N = [['post', 'Post'], ['caroselli', 'Caroselli'], ['reel', 'Reel'], ['storie', 'Storie']];
const MESI = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];

const pad2 = (n) => String(n).padStart(2, '0');
function prossimoFeriale() {
  const d = new Date(); d.setDate(d.getDate() + 1);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function meseDopo(ds) {
  const d = new Date(`${ds}T00:00:00`); const m = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  return `${m.getFullYear()}-${pad2(m.getMonth() + 1)}`;
}

function Field({ label, hint, children, full }) {
  return (
    <label style={{ display: 'block', gridColumn: full ? '1 / -1' : 'auto', minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>{label}</span>
      {children}
      {hint && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-dim)', marginTop: 5 }}>{hint}</span>}
    </label>
  );
}

function Contatore({ label, value, onChange, max = 60 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg2)' }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>al mese</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button type="button" className="eden-btn is-icon is-sm" aria-label={`Meno ${label}`} onClick={() => onChange(Math.max(0, value - 1))} disabled={value <= 0}><Minus size={14} /></button>
        <span style={{ width: 30, textAlign: 'center', fontSize: 17, fontWeight: 700, color: value ? 'var(--text)' : 'var(--text-dim)', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        <button type="button" className="eden-btn is-icon is-sm" aria-label={`Più ${label}`} onClick={() => onChange(Math.min(max, value + 1))}><Plus size={14} /></button>
      </div>
    </div>
  );
}

function Spunta({ checked, onChange, label, sub }) {
  return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', border: `1px solid ${checked ? 'var(--cyan)' : 'var(--border)'}`, borderRadius: 12, background: checked ? 'var(--cyan-dim)' : 'var(--bg2)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ width: 18, height: 18, marginTop: 1, accentColor: 'var(--cyan)', flexShrink: 0 }} />
      <span>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
        {sub && <span style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginTop: 2 }}>{sub}</span>}
      </span>
    </label>
  );
}

export default function OnboardingWizard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success } = useToast();
  const isMobile = useIsMobile();
  const isManager = MANAGER.includes(user?.ruolo);
  const isAdmin = user?.ruolo === 'admin';
  const [passo, setPasso] = useState(0);
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState([]);

  const [cliente, setCliente] = useState({ azienda: '', nome: '', email: '', telefono: '', sito: '', settore: '' });
  const [brand, setBrand] = useState({ obiettivi: '', target: '', tono: '', colori: '', competitor: '', evitare: '' });
  const [social, setSocial] = useState({});
  const [accessi, setAccessi] = useState({});
  const [servizi, setServizi] = useState({ post: 8, caroselli: 4, reel: 4, storie: 0, shooting: true, ads: false });
  const [squadra, setSquadra] = useState({ account_id: user?.id || '', smm_id: '', videomaker_id: '' });
  const kickoffDefault = useMemo(prossimoFeriale, []);
  const [avvio, setAvvio] = useState({ data_kickoff: kickoffDefault, crea_ped_bozza: true, mese_ped: meseDopo(kickoffDefault) });
  const [contratto, setContratto] = useState({ valore_mensile: '', prima_fattura: false, contenuti_quota: null, contenuti_periodo: 'mese' });
  const [note, setNote] = useState('');

  useEffect(() => {
    api.get('/team', { silent: true }).then(r => {
      const attivi = (r.data || []).filter(u => u.attivo !== false);
      setTeam(attivi);
      // Se c'è un solo social media manager / videomaker, lo propone già
      const unico = (ruolo) => { const l = attivi.filter(u => u.ruolo === ruolo); return l.length === 1 ? String(l[0].id) : ''; };
      setSquadra(s => ({ ...s, smm_id: s.smm_id || unico('social_manager'), videomaker_id: s.videomaker_id || unico('videomaker') }));
    }).catch(() => {});
  }, []);

  const nome = (id) => { const u = team.find(x => String(x.id) === String(id)); return u ? `${u.nome || ''} ${u.cognome || ''}`.trim() : ''; };
  const totContenuti = servizi.post + servizi.caroselli + servizi.reel + servizi.storie;
  // Contenuti da contratto: finché non lo cambi, segue post + caroselli + reel al mese
  const quotaContratto = contratto.contenuti_quota ?? (servizi.post + servizi.caroselli + servizi.reel);
  const puoAvanzare = passo !== 0 || cliente.azienda.trim().length > 0;
  const ultimo = passo === PASSI.length - 1;

  const avvia = async () => {
    setSaving(true);
    try {
      const body = {
        cliente, brand, social, accessi, servizi, note: note || null,
        team: { account_id: squadra.account_id || null, smm_id: squadra.smm_id || null, videomaker_id: squadra.videomaker_id || null },
        avvio: { data_kickoff: avvio.data_kickoff || null, crea_ped_bozza: avvio.crea_ped_bozza && totContenuti > 0, mese_ped: avvio.mese_ped || null },
        contratto: {
          valore_mensile: contratto.valore_mensile === '' ? null : Number(contratto.valore_mensile),
          prima_fattura: isAdmin && contratto.prima_fattura,
          contenuti_quota: quotaContratto === '' ? 0 : Number(quotaContratto),
          contenuti_periodo: contratto.contenuti_periodo,
        },
      };
      const r = await api.post('/onboarding/clienti', body);
      success(`${cliente.azienda} è pronto: ${r.data.task} passi di avvio${r.data.contenuti ? ` e ${r.data.contenuti} contenuti in bozza` : ''}`);
      navigate(`/clienti/${r.data.cliente_id}`);
    } catch { /* toast globale */ }
    setSaving(false);
  };

  const P = PASSI[passo];
  const selUtente = (campo) => (
    <select className="eden-input" value={squadra[campo]} onChange={e => setSquadra(s => ({ ...s, [campo]: e.target.value }))} disabled={campo === 'account_id' && !isManager}>
      <option value="">Nessuno</option>
      {team.map(u => <option key={u.id} value={u.id}>{`${u.nome || ''} ${u.cognome || ''}`.trim()}{String(u.id) === String(user?.id) ? ' (tu)' : ''}</option>)}
    </select>
  );

  return (
    <div className="eden-page eden-page--narrow" style={{ paddingBottom: isMobile ? 120 : undefined }}>
      <div style={{ marginBottom: 18 }}>
        <h1 style={{ margin: 0 }}>Onboarding nuovo cliente</h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>In pochi passi: cliente, brand, accessi, servizi e team. Al termine il team trova tutto pronto.</div>
      </div>

      {/* Avanzamento */}
      {isMobile ? (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 6 }}>
            <span>Passo {passo + 1} di {PASSI.length}</span><b style={{ color: 'var(--text)' }}>{P.label}</b>
          </div>
          <div style={{ height: 6, borderRadius: 6, background: 'var(--bg3)', overflow: 'hidden' }}>
            <div style={{ width: `${((passo + 1) / PASSI.length) * 100}%`, height: '100%', background: 'var(--cyan)', transition: 'width 0.25s var(--ease)' }} />
          </div>
        </div>
      ) : (
        <ol style={{ display: 'flex', gap: 6, listStyle: 'none', padding: 0, margin: '0 0 18px' }}>
          {PASSI.map((s, i) => {
            const fatto = i < passo; const attivo = i === passo;
            return (
              <li key={s.key} style={{ flex: 1 }}>
                <button type="button" onClick={() => i < passo && setPasso(i)} disabled={i > passo}
                  style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, background: 'none', border: 'none', padding: 0, cursor: i < passo ? 'pointer' : 'default', font: 'inherit', minHeight: 0 }}>
                  <span style={{ width: '100%', height: 4, borderRadius: 4, background: attivo || fatto ? 'var(--cyan)' : 'var(--bg3)', opacity: fatto ? 0.6 : 1 }} />
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: attivo ? 700 : 500, color: attivo ? 'var(--text)' : fatto ? 'var(--text-muted)' : 'var(--text-dim)' }}>
                    {fatto ? <Check size={13} color="var(--cyan)" /> : <s.Icon size={13} />}{s.label}
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      )}

      <section className="eden-card" style={{ padding: isMobile ? '18px 16px' : '22px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <P.Icon size={18} color="var(--cyan)" />
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 650, color: 'var(--text)' }}>{P.label}</h2>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 18 }}>{P.desc}</div>

        {P.key === 'azienda' && (
          <div className="form-grid-2" style={{ gap: 14 }}>
            <Field label="Nome azienda *" full>
              <input className="eden-input" autoFocus value={cliente.azienda} onChange={e => setCliente(c => ({ ...c, azienda: e.target.value }))} placeholder="es. Orologeria Bianchi" />
            </Field>
            <Field label="Referente"><input className="eden-input" value={cliente.nome} onChange={e => setCliente(c => ({ ...c, nome: e.target.value }))} placeholder="Nome e cognome" /></Field>
            <Field label="Settore"><input className="eden-input" value={cliente.settore} onChange={e => setCliente(c => ({ ...c, settore: e.target.value }))} placeholder="es. Orologi di lusso" /></Field>
            <Field label="Email"><input className="eden-input" type="email" value={cliente.email} onChange={e => setCliente(c => ({ ...c, email: e.target.value }))} placeholder="nome@azienda.it" /></Field>
            <Field label="Telefono"><input className="eden-input" type="tel" value={cliente.telefono} onChange={e => setCliente(c => ({ ...c, telefono: e.target.value }))} placeholder="+39 …" /></Field>
            <Field label="Sito web" full><input className="eden-input" value={cliente.sito} onChange={e => setCliente(c => ({ ...c, sito: e.target.value }))} placeholder="https://…" /></Field>
          </div>
        )}

        {P.key === 'brand' && (
          <div className="form-grid-2" style={{ gap: 14 }}>
            <Field label="Obiettivi" full hint="Cosa vuole ottenere il cliente nei prossimi 3-6 mesi.">
              <textarea className="eden-input" rows={3} value={brand.obiettivi} onChange={e => setBrand(b => ({ ...b, obiettivi: e.target.value }))} placeholder="es. più richieste in negozio, far conoscere la nuova collezione…" />
            </Field>
            <Field label="Pubblico"><textarea className="eden-input" rows={3} value={brand.target} onChange={e => setBrand(b => ({ ...b, target: e.target.value }))} placeholder="Chi vogliamo raggiungere" /></Field>
            <Field label="Tono di voce"><textarea className="eden-input" rows={3} value={brand.tono} onChange={e => setBrand(b => ({ ...b, tono: e.target.value }))} placeholder="es. elegante, ironico, tecnico…" /></Field>
            <Field label="Colori e stile visivo"><textarea className="eden-input" rows={2} value={brand.colori} onChange={e => setBrand(b => ({ ...b, colori: e.target.value }))} placeholder="Palette, fotografia, riferimenti" /></Field>
            <Field label="Competitor e riferimenti"><textarea className="eden-input" rows={2} value={brand.competitor} onChange={e => setBrand(b => ({ ...b, competitor: e.target.value }))} placeholder="Profili a cui ispirarsi o da cui distinguersi" /></Field>
            <Field label="Da evitare" full><input className="eden-input" value={brand.evitare} onChange={e => setBrand(b => ({ ...b, evitare: e.target.value }))} placeholder="Argomenti, parole o stili da non usare" /></Field>
            <div style={{ gridColumn: '1 / -1', fontSize: 12, color: 'var(--text-dim)' }}>Tutto facoltativo: si può completare dopo dalla scheda cliente (sezione Strategia).</div>
          </div>
        )}

        {P.key === 'social' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-grid-2" style={{ gap: 14 }}>
              {SOCIAL.map(([k, label, ph]) => (
                <Field key={k} label={label}><input className="eden-input" value={social[k] || ''} onChange={e => setSocial(s => ({ ...s, [k]: e.target.value }))} placeholder={ph} /></Field>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>Cosa abbiamo già ricevuto?</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {ACCESSI.map(([k, label]) => (
                  <Spunta key={k} checked={!!accessi[k]} onChange={v => setAccessi(a => ({ ...a, [k]: v }))} label={label} />
                ))}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginTop: 8 }}>Quello che manca diventa un passo della checklist, assegnato al team.</div>
            </div>
          </div>
        )}

        {P.key === 'servizi' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              {SERVIZI_N.map(([k, label]) => (
                <Contatore key={k} label={label} value={servizi[k]} max={k === 'storie' ? 120 : 60} onChange={v => setServizi(s => ({ ...s, [k]: v }))} />
              ))}
            </div>
            <Spunta checked={servizi.shooting} onChange={v => setServizi(s => ({ ...s, shooting: v }))} label="Shooting foto e video" sub="Aggiunge alla checklist l'organizzazione dello shooting iniziale per il Videomaker." />
            <Spunta checked={servizi.ads} onChange={v => setServizi(s => ({ ...s, ads: v }))} label="Gestione campagne ads" />
            <Field label="Contenuti da contratto" hint="Storie escluse. Nel Piano Editoriale vedrai quanti ne mancano ogni settimana o mese.">
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="eden-input" type="number" min="0" max="500" style={{ width: 96 }} aria-label="Numero di contenuti"
                  value={quotaContratto} onChange={e => setContratto(c => ({ ...c, contenuti_quota: e.target.value }))} />
                <div className="eden-seg" role="group" aria-label="Periodo">
                  {[['settimana', 'a settimana'], ['mese', 'al mese']].map(([k, l]) => (
                    <button key={k} type="button" className={contratto.contenuti_periodo === k ? 'is-on' : ''}
                      onClick={() => setContratto(c => ({ ...c, contenuti_periodo: k }))}>{l}</button>
                  ))}
                </div>
              </div>
            </Field>
            {isManager && (
              <Field label="Valore mensile del contratto (€)" hint="Visibile solo ai responsabili.">
                <input className="eden-input" type="number" min="0" value={contratto.valore_mensile} onChange={e => setContratto(c => ({ ...c, valore_mensile: e.target.value }))} placeholder="es. 1500" />
              </Field>
            )}
          </div>
        )}

        {P.key === 'team' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            <div className="form-grid-3" style={{ gap: 14 }}>
              <Field label="Account (referente)" hint={!isManager ? 'Sei tu il referente.' : undefined}>{selUtente('account_id')}</Field>
              <Field label="Social Media Manager">{selUtente('smm_id')}</Field>
              <Field label="Videomaker">{selUtente('videomaker_id')}</Field>
            </div>
            <div className="form-grid-2" style={{ gap: 14 }}>
              <Field label="Data del kick-off" hint="Le scadenze della checklist partono da qui (solo giorni feriali).">
                <input className="eden-input" type="date" value={avvio.data_kickoff} onChange={e => setAvvio(a => ({ ...a, data_kickoff: e.target.value, mese_ped: e.target.value ? meseDopo(e.target.value) : a.mese_ped }))} />
              </Field>
              <Field label="Primo mese di pubblicazione">
                <input className="eden-input" type="month" value={avvio.mese_ped} onChange={e => setAvvio(a => ({ ...a, mese_ped: e.target.value }))} />
              </Field>
            </div>
            <Spunta checked={avvio.crea_ped_bozza && totContenuti > 0} onChange={v => setAvvio(a => ({ ...a, crea_ped_bozza: v }))}
              label={`Crea la bozza del primo Piano Editoriale (${totContenuti} contenuti)`}
              sub="Contenuti in stato Idea distribuiti nei giorni feriali del mese, già assegnati a SMM e Videomaker." />
            {isAdmin && (
              <Spunta checked={contratto.prima_fattura} onChange={v => setContratto(c => ({ ...c, prima_fattura: v }))}
                label="Crea la prima fattura" sub={contratto.valore_mensile ? `€ ${contratto.valore_mensile} — mese del kick-off` : 'Imposta il valore mensile nel passo Servizi'} />
            )}
            <Field label="Note per il team"><textarea className="eden-input" rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder="Tutto quello che il team deve sapere prima di partire" /></Field>
          </div>
        )}

        {P.key === 'riepilogo' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { Icon: Building2, t: cliente.azienda || '—', s: [cliente.settore, cliente.nome, cliente.email].filter(Boolean).join(' · ') || 'Nessun dettaglio aggiuntivo' },
              { Icon: Users, t: 'Team', s: [['Account', squadra.account_id], ['SMM', squadra.smm_id], ['Videomaker', squadra.videomaker_id]].filter(([, v]) => v).map(([r, v]) => `${r}: ${nome(v) || '—'}`).join(' · ') || 'Nessuno assegnato' },
              { Icon: ListChecks, t: 'Checklist di avvio', s: `Kick-off${avvio.data_kickoff ? ` il ${new Date(avvio.data_kickoff + 'T00:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })}` : ''}, accessi mancanti, linea editoriale${servizi.shooting ? ', shooting' : ''}, primo PED, portale cliente` },
              ...(avvio.data_kickoff ? [{ Icon: CalendarDays, t: 'Kick-off in calendario', s: 'Evento alle 10:00 nel calendario del referente' }] : []),
              ...(avvio.crea_ped_bozza && totContenuti ? [{ Icon: FileText, t: `${totContenuti} contenuti in bozza`, s: `${MESI[Number(avvio.mese_ped.slice(5, 7)) - 1] || ''} ${avvio.mese_ped.slice(0, 4)} · ${SERVIZI_N.filter(([k]) => servizi[k]).map(([k, l]) => `${servizi[k]} ${l.toLowerCase()}`).join(', ')}` }] : []),
              ...(Number(quotaContratto) > 0 ? [{ Icon: FileSignature, t: 'Contratto', s: `${quotaContratto} contenuti ${contratto.contenuti_periodo === 'settimana' ? 'a settimana' : 'al mese'} (storie escluse)` }] : []),
              ...(isAdmin && contratto.prima_fattura && contratto.valore_mensile ? [{ Icon: Receipt, t: 'Prima fattura', s: `€ ${contratto.valore_mensile}` }] : []),
            ].map((x, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 12, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                <x.Icon size={17} color="var(--cyan)" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{x.t}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 2 }}>{x.s}</div>
                </div>
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Il team riceve una notifica. Tutto si può modificare dopo dalla scheda cliente.</div>
          </div>
        )}
      </section>

      {/* Navigazione tra i passi (su telefono sempre in basso, sopra la tab bar) */}
      <div style={isMobile
        ? { position: 'fixed', left: 0, right: 0, bottom: 'calc(var(--tabbar-h) + env(safe-area-inset-bottom))', zIndex: 150, display: 'flex', gap: 8, padding: '10px 16px', background: 'var(--topbar-bg)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)', borderTop: '1px solid var(--border)' }
        : { display: 'flex', gap: 8, marginTop: 16 }}>
        {passo > 0 ? (
          <button type="button" className="eden-btn" onClick={() => setPasso(p => p - 1)}><ArrowLeft size={15} />Indietro</button>
        ) : (
          <button type="button" className="eden-btn" onClick={() => navigate(-1)}>Annulla</button>
        )}
        <div style={{ flex: 1 }} />
        {ultimo ? (
          <button type="button" className="eden-btn is-primary" onClick={avvia} disabled={saving || !cliente.azienda.trim()} style={{ flex: isMobile ? 1 : 'none' }}>
            <Check size={16} />{saving ? 'Avvio…' : 'Avvia onboarding'}
          </button>
        ) : (
          <button type="button" className="eden-btn is-primary" onClick={() => setPasso(p => p + 1)} disabled={!puoAvanzare} style={{ flex: isMobile ? 1 : 'none' }}>
            Avanti<ArrowRight size={15} />
          </button>
        )}
      </div>
    </div>
  );
}
